import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';

import { langSelector } from 'selectors/lang';
import siteActions from 'actions/site-actions';
import { ActionSuccessType, ActionFailureType, GET_CONTENTS, GET_SESSIONS_CONTENTS } from 'constants/redux-actions';
import AxiosClient from 'configurations/api-client';
import { API_GET_CONTENTS, API_GET_SESSIONS_CONTENTS } from 'constants/api-paths';

const useGetContentsFromCDN = (prefixKey, staticJsonUrl) =>
  useQuery({
    queryKey: [staticJsonUrl],
    queryFn: async ({ signal }) => {
      if (!staticJsonUrl) {
        throw new Error('No staticJsonUrl provided');
      }

      const response = await fetch(staticJsonUrl, {
        method: 'GET',
        signal,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch contents from CDN: HTTP ' + response.status);
      }

      const data = await response.json();
      return data;
    },
    enabled: !isEmpty(staticJsonUrl) && !!prefixKey,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1_000,
    retry: false,
  });

export const useGetContents = (siteId, prefixKey, options = {}) => {
  const dispatch = useDispatch();

  const { loaded: isLoadedSiteLang, selectedLang } = useSelector(langSelector);

  const {
    data: cdnData,
    isSuccess: cdnSuccess,
    isFetching: cdnFetching,
  } = useGetContentsFromCDN(prefixKey, options?.staticJsonUrl);

  const {
    data: contents = [],
    isFetching: isLoading,
    isSuccess,
    isError,
  } = useQuery({
    queryKey: ['contents', siteId, prefixKey, selectedLang],
    queryFn: async () => {
      if (!isEmpty(cdnData) && cdnSuccess) {
        const langId = selectedLang || 0;
        return cdnData[langId] || cdnData[0] || [];
      }

      const response = await AxiosClient.get(API_GET_CONTENTS, { params: { siteId, prefixKey } });

      return get(response, 'data', []);
    },
    enabled: !!siteId && !!isLoadedSiteLang && !options?.customPage && !cdnFetching,
    refetchOnWindowFocus: false,
    staleTime: 15 * 60 * 1000,
    cacheTime: 20 * 60 * 1000,
  });

  useEffect(() => {
    if (isSuccess) {
      dispatch({
        type: ActionSuccessType(GET_CONTENTS),
        payload: {
          prefixKey,
          data: contents,
        },
      });
    }
  }, [siteId, contents, dispatch, isSuccess, prefixKey]);

  useEffect(() => {
    if (isError) {
      dispatch(siteActions.clearContents({ prefixKey }));
    }
  }, [dispatch, isError, prefixKey]);

  return { contents, isLoading };
};

export const useGetSessionsContents = () => {
  const dispatch = useDispatch();

  const {
    data: sessionsContents = [],
    isLoading,
    isSuccess,
    isError,
  } = useQuery({
    queryKey: ['sessionsContents'],
    queryFn: async () => {
      const response = await AxiosClient.get(API_GET_SESSIONS_CONTENTS);

      return get(response, 'data', []);
    },
    refetchOnWindowFocus: false,
    staleTime: 15 * 60 * 1000,
    cacheTime: 20 * 60 * 1000,
  });

  useEffect(() => {
    if (isSuccess) {
      dispatch({
        type: ActionSuccessType(GET_SESSIONS_CONTENTS),
        payload: {
          data: sessionsContents,
        },
      });
    }
  }, [sessionsContents, dispatch, isSuccess]);

  useEffect(() => {
    if (isError) {
      dispatch({
        type: ActionFailureType(GET_SESSIONS_CONTENTS),
        payload: {
          data: [],
        },
      });
    }
  }, [dispatch, isError]);

  return { sessionsContents, isLoading };
};
